// src/components/Sidebar.js
import React, { useContext, useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from './AuthContext';
import {
	FiHome,
	FiLayers,
	FiHelpCircle,
	FiMail,
	FiChevronDown,
	FiGlobe,
	FiPlusCircle,
	FiBookOpen,
	FiTarget,
	FiShield,
	FiFileText,
	FiLogOut,
	FiUser,
	FiActivity,
	FiDownload,
	FiEdit3,
	FiFlag,
	FiRefreshCw,
} from 'react-icons/fi';
import { FaUserShield } from 'react-icons/fa';
import { PiRanking, PiTrophy } from 'react-icons/pi';
import '../styles/Sidebar.css';
import languageService from '../services/languageService';

const Sidebar = ({ isMobileOpen, onCloseMobile, isCollapsed, onToggleCollapse }) => {
	const { t, i18n } = useTranslation();
	const { isLoggedIn, logout, role } = useContext(AuthContext);
	const [isLoggingOut, setIsLoggingOut] = useState(false);
	const navigate = useNavigate();

	const [leaguesOpen, setLeaguesOpen] = useState(false);
	const [adminOpen, setAdminOpen] = useState(false);
	const [faqOpen, setFaqOpen] = useState(false);
	const [langOpen, setLangOpen] = useState(false);
	const langRef = useRef(null);

	const closeOnClick = () => onCloseMobile && onCloseMobile();

	const toggleLeagues = () => {
		// v collapsed režimu se nejdřív rozbal, pak ukaž submenu
		if (isCollapsed && onToggleCollapse) {
			onToggleCollapse();
		}
		setLeaguesOpen(v => !v);
	};

	const toggleAdmin = () => {
		if (isCollapsed && onToggleCollapse) {
			onToggleCollapse();
		}
		setAdminOpen(v => !v);
	};

	const toggleFaq = () => {
		if (isCollapsed && onToggleCollapse) {
			onToggleCollapse();
		}
		setFaqOpen(v => !v);
	};

	const handleLogout = async () => {
		if (isLoggingOut) return;

		setIsLoggingOut(true);

		try {
			// Show confirmation for logout
			const loggedOut = await logout(true); // true = show confirmation

			if (loggedOut) {
				if (onCloseMobile) onCloseMobile();
				navigate('/');
			}
		} catch (error) {
			console.error('Logout failed:', error);
			navigate('/');
		} finally {
			setIsLoggingOut(false);
		}
	};

	useEffect(() => {
		const onDocClick = (e) => {
			if (!langRef.current) return;
			if (!langRef.current.contains(e.target)) setLangOpen(false);
		};
		document.addEventListener('click', onDocClick);
		return () => document.removeEventListener('click', onDocClick);
	}, []);

	useEffect(() => {
		// Při přepnutí do collapsed režimu zavřeme otevřená submenu
		if (isCollapsed) {
			setLeaguesOpen(false);
			setAdminOpen(false);
			setFaqOpen(false);
		}
	}, [isCollapsed]);

	const setLang = (lng) => {
		try {
			languageService.changeLanguageAndPersist(i18n, lng);
		} catch (e) {
			console.warn('Language change failed:', e);
		}
		setLangOpen(false);
	};

	const currentLangCode = (i18n && i18n.language ? i18n.language : 'cs').slice(0, 2).toUpperCase();

	return (
		<aside id="app-sidebar" className={`sidebar ${isMobileOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
			<div className="sidebar-header">
				<NavLink to="/" className="sidebar-logo hide-on-mobile" onClick={closeOnClick}>
					<span className="logo-full">Scoreo</span>
					<span className="logo-short">S</span>
				</NavLink>
				{!isCollapsed && (
					<div className="lang-switch hide-on-mobile" ref={langRef}>
						<button
							type="button"
							className="lang-trigger"
							aria-haspopup="menu"
							aria-expanded={langOpen}
							onClick={() => setLangOpen(v => !v)}
							title="Změnit jazyk"
						>
							<FiGlobe className="lang-icon" />
							<span className="lang-code">{currentLangCode}</span>
						</button>
						{langOpen && (
							<div className="lang-menu" role="menu">
								<button className="lang-item" role="menuitem" onClick={() => setLang('cs')}>Čeština</button>
								<button className="lang-item" role="menuitem" onClick={() => setLang('en')}>English</button>
							</div>
						)}
					</div>
				)}
			</div>

			<nav className="sidebar-nav">
				{/* HORNÍ ČÁST */}
				<ul>
					<li className="nav-item">
						<NavLink
							to="/dashboard"
							className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
							onClick={closeOnClick}
						>
							<FiHome className="nav-icon" />
							<span>{t('dashboard') || 'Dashboard'}</span>
						</NavLink>
					</li>

					{/* Ligy (rozeviraci) */}
					<li className={`nav-item has-submenu ${leaguesOpen ? 'open' : ''}`}>
						<button
							className="nav-link-wrapper"
							onClick={toggleLeagues}
							aria-expanded={leaguesOpen}
							aria-controls="submenu-leagues"
						>
							<FiLayers className="nav-icon" />
							<span>{t('leagues.menu') || 'Ligy'}</span>
							<FiChevronDown className="dropdown-arrow" />
						</button>
						{leaguesOpen && !isCollapsed && (
							<ul id="submenu-leagues" className="submenu">
								<li>
									<NavLink
										to="/public-leagues"
										className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
										onClick={closeOnClick}
									>
										<FiGlobe className="nav-icon" />
										<span>{t('publicLeagues') || 'Veřejné ligy'}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to="/create-league"
										className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
										onClick={closeOnClick}
									>
										<FiPlusCircle className="nav-icon" />
										<span>{t('createLeague') || 'Vytvořit ligu'}</span>
									</NavLink>
								</li>
							</ul>
						)}
					</li>

					<li className="nav-item">
						<NavLink
							to="/leaderboard"
							className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
							onClick={closeOnClick}
						>
							<PiRanking className="nav-icon" />
							<span>{t('rankings') || 'Žebříčky'}</span>
						</NavLink>
					</li>

					<li className="nav-item">
						<NavLink
							to="/achievements"
							className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
							onClick={closeOnClick}
						>
							<PiTrophy className="nav-icon" />
							<span>{t('achievements') || 'Trofeje'}</span>
						</NavLink>
					</li>
					
					{(role === 'ADMIN') && (
						<>
							<div className="sidebar-divider" style={{ margin: '10px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}></div>

							<li className={`nav-item has-submenu ${adminOpen ? 'open' : ''}`}>
								<button
									className="nav-link-wrapper admin-link"
									onClick={toggleAdmin}
									aria-expanded={adminOpen}
									aria-controls="submenu-admin"
								>
									<FaUserShield className="nav-icon" />
									<span>Admin Panel</span>
									<FiChevronDown className="dropdown-arrow" />
								</button>
								{adminOpen && !isCollapsed && (
									<ul id="submenu-admin" className="submenu">
										<li>
											<NavLink
												to="/admin/dashboard"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiActivity className="nav-icon" />
												<span>Přehled</span>
											</NavLink>
										</li>
										<li>
											<NavLink
												to="/admin/import-matches"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiDownload className="nav-icon" />
												<span>Import zápasů</span>
											</NavLink>
										</li>
										<li>
											<NavLink
												to="/admin/edit-match"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiEdit3 className="nav-icon" />
												<span>Úprava zápasu</span>
											</NavLink>
										</li>
										<li>
											<NavLink
												to="/admin/update-match-data"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiRefreshCw className="nav-icon" />
												<span>Aktualizace dat zápasu</span>
											</NavLink>
										</li>
										<li>
											<NavLink
												to="/admin/create-tournament"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiFlag className="nav-icon" />
												<span>Nový turnaj</span>
											</NavLink>
										</li>
										<li>
											<NavLink
												to="/admin/update-tournament"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiEdit3 className="nav-icon" />
												<span>Úprava turnaje</span>
											</NavLink>
										</li>
										<li>
											<NavLink
												to="/admin/tournament-rounds"
												className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
												onClick={closeOnClick}
											>
												<FiLayers className="nav-icon" />
												<span>Multiplikátory kol</span>
											</NavLink>
										</li>
									</ul>
								)}
							</li>
						</>
					)}
				</ul>

				{/* SPODNÍ ČÁST */}
				<ul className="bottom-nav">
					{isLoggedIn && (
						<li className="nav-item">
							<NavLink
								to="/profile"
								className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
								onClick={closeOnClick}
							>
								<FiUser className="nav-icon" />
								<span>{t('profile') || 'Profil'}</span>
							</NavLink>
						</li>
					)}

					{/* FAQ (rozeviraci) */}
					<li className={`nav-item has-submenu ${faqOpen ? 'open' : ''}`}>
						<button
							className="nav-link-wrapper"
							onClick={toggleFaq}
							aria-expanded={faqOpen}
							aria-controls="submenu-faq"
						>
							<FiHelpCircle className="nav-icon" />
							<span>FAQ</span>
							<FiChevronDown className="dropdown-arrow" />
						</button>
						{faqOpen && !isCollapsed && (
							<ul id="submenu-faq" className="submenu">
								<li>
									<NavLink
										to="/rules"
										className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
										onClick={closeOnClick}
									>
										<FiBookOpen className="nav-icon" />
										<span>{t('rules.title') || 'Pravidla hry'}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to="/scoring"
										className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
										onClick={closeOnClick}
									>
										<FiTarget className="nav-icon" />
										<span>{t('scoring.title', { defaultValue: 'Bodování' })}</span>
									</NavLink>
								</li>
								<li>
									<NavLink
										to="/legal"
										className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`}
										target="_blank"
										rel="noopener noreferrer"
									>
										<FiShield className="nav-icon" />
										<span>{t('terms') || 'Podmínky a soukromí'}</span>
									</NavLink>
								</li>
							</ul>
						)}
					</li>
					<li className="nav-item">
						<NavLink
							to="/contact"
							className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
							onClick={closeOnClick}
						>
							<FiMail className="nav-icon" />
							<span>{t('contact') || 'Kontakt'}</span>
						</NavLink>
					</li>

					{isLoggedIn && (
						<li className="nav-item">
							<button
								onClick={handleLogout}
								className={`logout-button logout-link ${isLoggingOut ? 'loading' : ''}`}
								disabled={isLoggingOut}
							>
								<FiLogOut className="nav-icon" />
								<span>
									{isLoggingOut
										? (t('loggingOut'))
										: (t('logout'))
									}
								</span>
							</button>
						</li>
					)}
				</ul>
			</nav>
		</aside>
	);
};

export default Sidebar;
